import { defineStore } from "pinia";
import {
  fetchOnboardingStatus,
  setOnboardingStep,
  completeOnboarding,
  type OnboardingStatus,
} from "@/services/onboardingApi";

export const useOnboardingStore = defineStore("onboarding", {
  state: (): {
    status: OnboardingStatus | null;
    loading: boolean;
    lastFetchedFor: number | null;
  } => ({
    status: null,
    loading: false,
    lastFetchedFor: null,
  }),

  getters: {
    isCompleted: (s) => s.status?.completed === true,
    needsOnboarding: (s) => s.status !== null && s.status.completed === false,
    currentStep: (s) => s.status?.currentStep ?? "welcome",
  },

  actions: {
    async ensureLoaded(idOrganization: number, force = false) {
      if (!force && this.status && this.lastFetchedFor === idOrganization) return this.status;
      this.loading = true;
      try {
        this.status = await fetchOnboardingStatus();
        this.lastFetchedFor = idOrganization;
        return this.status;
      } finally {
        this.loading = false;
      }
    },

    async setStep(step: string) {
      if (this.status) this.status.currentStep = step;
      try {
        await setOnboardingStep(step);
      } catch (e) {
        // Non-critical: persistence error should not block the UI flow.
        console.warn("Onboarding: failed to persist step", e);
      }
    },

    async finish() {
      await completeOnboarding();
      if (this.status) {
        this.status.completed = true;
        this.status.currentStep = "completed";
        this.status.completedAt = new Date().toISOString();
      }
    },

    reset() {
      this.status = null;
      this.lastFetchedFor = null;
    },
  },
});
